import { ADMIN_SYSTEM_PROBLEMS, Div, NoticeBanner } from '@vgrubs/components';
import { useNavigate } from 'react-router-dom';
import { useDashboardSystemProblems } from '../hooks/useDashboardSystemProblems';
import { useSystemProblemsSocket } from '../hooks/useSystemProblemsSocket';

/**
 * At the top of the Dashboard's Orders tab: nothing at all, unless a critical
 * system problem is open - then one banner with how many and the most urgent
 * one's title, and a click lands on the System Problems page showing just the
 * open critical ones. Warnings and info wait on that page without interrupting
 * the Orders view.
 *
 * Drawn with NoticeBanner, the strip the SaaS and vDrive debt notices are made
 * of, so a Dashboard alarm looks the same whatever it is about. The whole
 * strip is the click target (no button), as for a billing banner with an
 * action and no Pay.
 *
 * Kept live by the socket like the tab itself, so a problem that opens or
 * closes while someone looks at the orders shows up or goes away.
 */
export const UrgentProblemsBar = () => {
  useSystemProblemsSocket();
  const navigate = useNavigate();
  const { bySeverity, items } = useDashboardSystemProblems(null);

  const critical = bySeverity?.critical ?? 0;
  if (critical === 0) return null;

  /* The card's items are most severe first, so the first is a critical one. */
  const first = items.find((item) => item.severity === 'critical');

  return (
    <Div $m="32px 0 0" $mSM="20px 0 0">
      <NoticeBanner
        critical
        title={
          critical === 1
            ? '1 critical system problem'
            : `${critical} critical system problems`
        }
        message={
          first
            ? `${first.title}${
                critical > 1 ? ` and ${critical - 1} more` : ''
              }. Open System Problems to deal with them.`
            : 'Open System Problems to deal with them.'
        }
        onActivate={() =>
          navigate(
            `${ADMIN_SYSTEM_PROBLEMS}?problemStatus=open&problemSeverity=critical`
          )
        }
      />
    </Div>
  );
};
